/* global _satellite */
import React from 'react'
import isEmpty from 'lodash/isEmpty'
import { getLocalPath } from '../../../utility'
import { isInvestor } from '../../../../global/authentication/authentication-manager'
import { safeFetch } from '../../../../global/data/service.js'

const updateArray = (array, entry, add) => {
  const indexOfEntry = array.indexOf(entry)

  if (indexOfEntry > -1) {
    if (!add) {
      array.splice(indexOfEntry, 1)
    }
  } else {
    if (add) {
      array.push(entry)
    }
  }
}

const titleCase = (str) => {
  return (
    (str &&
      str
        .toLowerCase()
        .split(' ')
        .map(function (word) {
          return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(' ')) ||
    ''
  )
}

const convertDate = (d) => {
  const date = new Date(d)
  if (d && date instanceof Date && !isNaN(date)) {
    const dateSplit = d.split('-')
    const day = dateSplit[2].split('T')[0]

    return `${dateSplit[1]}/${day}/${dateSplit[0]}`
  }

  return ''
}

const renderInvestorGoalsIcons = (fund) => {
  if (!isInvestor()) {
    const filter =
      fund &&
      fund.filters &&
      fund.filters.find((filter) => filter.filter === 'investorGoal')

    if (filter && filter.filterValue) {
      return (
        <>
          {filter.filterValue.map((filterVal) => {
            return (
              <div
                key={filterVal.code}
                className={
                  'icon-32 ' +
                  'icon-' +
                  lookupIcon(filterVal.code) +
                  '-blue-ltblue'
                }
              />
            )
          })}
        </>
      )
    }
  }

  return null
}

const lookupCategory = (categoryname) => {
  switch (categoryname) {
    case 'Domestic Equity':
      return 'domestic-equity'
    case 'International Equity':
    case 'Global/International Equity':
      return 'international-equity'
    case 'Sector Equity/Specialty':
      return 'sector-equity'
    case 'Asset Allocation/Alternative':
    case 'Asset Allocation':
      return 'asset-allocation'
    case 'Alternative':
      return 'alternative'
    case 'Taxable Fixed Income':
      return 'taxable-fixed-income'
    case 'Tax-Exempt Fixed Income':
      return 'tax-exempt-fixed-income'
    case 'Money Market':
      return 'money-market'
    default:
      return ''
  }
}

const lookupIcon = (code) => {
  switch (code.trim()) {
    case 'IG1':
      return 'plan-retirement'
    case 'IG5':
      return 'grow-assets'
    case 'IG4':
      return 'navigate-rates'
    case 'IG3':
      return 'ease-impact'
    case 'IG2':
      return 'maximize-returns'
    default:
      return ''
  }
}

const filterAnalyticsTag = (pathname) => {
  if (pathname.includes('exchange-traded-funds')) {
    return 'exchange-traded-funds-filter-checkbox'
  } else if (pathname.includes('investor')) {
    return 'investor-mutual-funds-filter-checkbox'
  } else {
    return 'mutual-funds-filter-checkbox'
  }
}

const filterAnalyticsSatellite = (tagString, pathname) => {
  if (pathname.includes('exchange-traded-funds')) {
    _satellite.track('exchangeTradedFundsViewResults', tagString)
  } else if (pathname.includes('investor')) {
    _satellite.track('investorsMutualFundsViewResults', tagString)
  } else {
    _satellite.track('mutualFundsViewResults', tagString)
  }
}

const hasFundDistributionsEstimates = (fund, unit) => !!fund?.[unit]

const formatToFixed = (n, d = 2) =>
  new Number(n).toLocaleString('en-US', {
    useGrouping: false,
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  })

const isPresentFundEstimates = (funds) =>
  funds.some((i) => !isEmpty(i?.estimates))

const getTabNames = async (product) => {
  try {
    const url = `${getLocalPath()}/halapi/documents?_scope=/content/documents/administration/cti-common&_expr=jcr:contains(@resourcebundle:id, '${product}.product.landing')`
    const response = await safeFetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, url: ${url}`)
    }

    return await response.json()
  } catch {
    return {}
  }
}

const getProductTypes = async () => {
  try {
    const url = `${getLocalPath()}/halapi/documents?_scope=/content/documents/administration/cti-common&_expr=jcr:contains(@resourcebundle:id, 'product.types')`
    const response = await safeFetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, url: ${url}`)
    }

    return await response.json()
  } catch {
    return {}
  }
}

const getFactsheetUrls = async (product, selectedClass) => {
  try {
    const url = `${getLocalPath()}/halapi/documents?_scope=/content/documents/cti-advisor/products/${product}/classes&_sort=ctifund:title&_expr=@ctifund:status='1'${selectedClass ? `,@ctifund:fundClass='${selectedClass}'` : ''}&_limit=-1&_fields=cusipId,productDocuments`
    const response = await safeFetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, url: ${url}`)
    }

    return await response.json()
  } catch {
    return {}
  }
}

const mapFactsheetUrls = async (product, selectedClass) => {
  const docs =
    (await getFactsheetUrls(product, selectedClass))._embedded?.documents || []
  const map = {}
  docs.forEach((doc) => {
    let factsheet = doc.productDocuments?.find(
      (doc) => doc.docType === 'Fact Sheet'
    )
    if (factsheet?.file?._meta?.mirror?.path) {
      map[doc.cusipId] = {
        path: factsheet?.file?._meta?.mirror?.path,
        displayAdvisor: factsheet?.displayAdvisor,
        displayInvestor: factsheet?.displayInvestor,
      }
    }
  })
  return map
}

const getPerformanceDisclosureDoc = async (product) => {
  const site = isInvestor() ? 'investor' : 'advisor'
  try {
    const url = `${getLocalPath()}/halapi/documents?_scope=/content/documents/cti-${site}/products/${product}/performance-disclosure`
    const response = await safeFetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, url: ${url}`)
    }

    return await response.json()
  } catch {
    return {}
  }
}

const get529SubHeaderDoc = async () => {
  const site = isInvestor() ? 'investor' : 'advisor'
  try {
    const url = `${getLocalPath()}/halapi/documents?_scope=/content/documents/cti-${site}/products/product-listing/529-plan-content`
    const response = await safeFetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, url: ${url}`)
    }

    return await response.json()
  } catch {
    return {}
  }
}

const stickyOnScroll = (setTableHeaderTop) => {
  const linksContainer = document.getElementById('fundHeader-linkSection')
  const titleEl = document.getElementById('spaProductHeaderTitle')

  // Guard: required DOM elements may not exist on all product page layouts.
  // Exit silently rather than throwing — sticky positioning simply won't apply.
  if (!titleEl) return

  const titleHeight = titleEl.offsetHeight
  const headerHeight = matchMedia('(min-width: 768px)').matches
    ? document.getElementById('spaProductHeader')?.offsetHeight || 0
    : (linksContainer?.offsetHeight ?? 0) > titleHeight
      ? linksContainer.offsetHeight
      : titleHeight

  // Scroll logic
  let scrollPosition = 0
  let scrollingUp
  window.addEventListener('scroll', () => {
    const productHeaderOffset = matchMedia('(min-width: 1025px)').matches
      ? document.getElementsByClassName('nav-container')[0]?.offsetHeight || 0
      : (document.getElementsByClassName('sticky-bar')[0]?.offsetHeight || 0) +
        (document.getElementsByClassName('MainHeader')[0]?.offsetHeight || 0)
    // Subnav height defined here or else it will always be 0
    const subnavHeight =
      document.getElementById('spaProductSubnav')?.offsetHeight || 0
    const bodyScrollTop = document.body.getBoundingClientRect().top
    scrollingUp = bodyScrollTop > scrollPosition
    scrollPosition = bodyScrollTop
    if (matchMedia('(min-width: 768px)').matches) {
      if (scrollingUp) {
        // When scrolling up, 'top' for the table header is lower down the page
        // because the product header is taller with links included
        setTableHeaderTop(productHeaderOffset + headerHeight + subnavHeight)
      } else {
        setTableHeaderTop(
          // When scrolling down, 'top' for the table header is higher up the
          // page because the product header is shorter with just the title
          productHeaderOffset + titleHeight + subnavHeight
        )
      }
    } else {
      setTableHeaderTop(null)
    }
  })
}

const bodyScrollBehavior = (isMobile, isFilterOpen) => {
  const shouldNotScroll = isMobile && isFilterOpen
  return shouldNotScroll ? 'hidden' : 'auto'
}

const getFundDisclosuresMapping = async () => {
  try {
    const url = `${getLocalPath()}/halapi/documents?_scope=/content/documents/administration/cti-common&_expr=jcr:contains(@resourcebundle:id, 'fund.disclosure.reference.map')`
    const response = await safeFetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, url: ${url}`)
    }

    return await response.json()
  } catch {
    return {}
  }
}

export {
  updateArray,
  titleCase,
  convertDate,
  renderInvestorGoalsIcons,
  lookupCategory,
  lookupIcon,
  filterAnalyticsTag,
  filterAnalyticsSatellite,
  hasFundDistributionsEstimates,
  formatToFixed,
  isPresentFundEstimates,
  getTabNames,
  getProductTypes,
  getPerformanceDisclosureDoc,
  stickyOnScroll,
  bodyScrollBehavior,
  get529SubHeaderDoc,
  getFundDisclosuresMapping,
  mapFactsheetUrls,
}
